import React, { useState, useCallback, useMemo } from "react";
import { css } from "@emotion/react";
import {
  Button,
  Heading,
} from "@phoenix/components";

// ==========================================
// SVGs & Icons
// ==========================================

export const ShellTargetIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#059669"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="18" rx="3" />
    <path d="M7 9l4 3-4 3" />
    <line x1="13" y1="15" x2="17" y2="15" />
  </svg>
);

const WifiIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#10b981"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CrossIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FormatAlignIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="15" y2="12" />
    <line x1="3" y1="18" x2="18" y2="18" />
  </svg>
);

const CodeBracketsIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const ChevronDownIcon = ({ style }: { style?: React.CSSProperties }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const AlertTriangleIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#ef4444"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// ==========================================
// Types & Defaults
// ==========================================

export interface ExecutableTargetConfigValues {
  command: string;
  configJson: string;
  env: Record<string, string>;
  timeout: number | string;
  testGenerationInstructions?: string;
  prompts?: string[];
  delayMs?: number | string;
  extensionHook?: string;
}

export const DEFAULT_EXECUTABLE_TARGET_CONFIG: ExecutableTargetConfigValues = {
  command: "exec:python /path/to/agent.py",
  configJson: "{}",
  env: {},
  timeout: 30000,
  testGenerationInstructions: "",
  prompts: ["{{prompt}}"],
  delayMs: 0,
  extensionHook: "",
};

export const EXECUTABLE_EXAMPLE_CONFIG = {
  env: {
    APP_ENV: "test",
    API_KEY: "{{MY_API_KEY}}",
  },
  timeout: 30000,
};

// ==========================================
// Formatting & Validation Helpers
// ==========================================

export function formatExecutableConfigJson(jsonStr: string): { formatted?: string; error?: string } {
  if (!jsonStr || !jsonStr.trim()) {
    return { formatted: "{}" };
  }
  try {
    const parsed = JSON.parse(jsonStr);
    return { formatted: JSON.stringify(parsed, null, 2) };
  } catch (err: any) {
    return { error: "Invalid JSON: " + (err?.message || "Syntax error") };
  }
}

export function validateExecutableTargetConfig(values: ExecutableTargetConfigValues): {
  isValid: boolean;
  errors: { command?: string; configJson?: string; timeout?: string };
} {
  const errors: { command?: string; configJson?: string; timeout?: string } = {};

  if (!values.command || !values.command.trim()) {
    errors.command = "Command is required (e.g. 'exec:python /path/to/agent.py' or './script.sh')";
  }

  if (values.configJson && values.configJson.trim() && values.configJson.trim() !== "{}") {
    try {
      JSON.parse(values.configJson);
    } catch (err: any) {
      errors.configJson = "Invalid JSON configuration: " + (err?.message || "Syntax error");
    }
  }

  if (values.timeout !== undefined && values.timeout !== "") {
    const tNum = Number(values.timeout);
    if (isNaN(tNum) || tNum <= 0) {
      errors.timeout = "Timeout must be a positive number";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

// ==========================================
// Component Props
// ==========================================

export interface ExecutableTargetConfigurationProps {
  targetName?: string;
  values: ExecutableTargetConfigValues;
  onChange: (updates: Partial<ExecutableTargetConfigValues>) => void;
  onBack: () => void;
  onNext: () => void;
  hideBottomBar?: boolean;
  renderModeSwitcher?: React.ReactNode;
  onTestConnection?: () => void;
  isTestingConnection?: boolean;
  testResult?: {
    success: boolean;
    message: string;
    latency?: string | number;
  } | null;
}

// ==========================================
// Emotion Styles
// ==========================================

const pageWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const pageHeaderTitle = css`
  margin: 0;
  font-size: 1.45rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const providerHeaderRow = css`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const providerBadgeTitle = css`
  font-size: 1.18rem;
  font-weight: 750;
  color: #0f172a;

  .theme--dark & {
    color: #f1f5f9 !important;
  }
`;

const formCardContainer = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);

  .theme--dark & {
    background: #0f172a !important;
    border-color: #1e293b !important;
  }
`;

const formGroup = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const fieldLabel = css`
  font-size: 0.92rem;
  font-weight: 700;
  color: #1e293b;

  .theme--dark & {
    color: #f1f5f9 !important;
  }
`;

const fieldHelpText = css`
  margin: 0;
  font-size: 0.83rem;
  color: #64748b;
  line-height: 1.45;

  a {
    color: #059669;
    text-decoration: underline;
    &:hover {
      color: #047857;
    }
  }

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const textInput = (hasError = false) => css`
  width: 100%;
  height: 40px;
  padding: 8px 12px;
  border: 1px solid ${hasError ? "#ef4444" : "#cbd5e1"};
  border-radius: 8px;
  font-size: 0.92rem;
  color: #0f172a;
  background: #ffffff;
  transition: all 0.15s ease-in-out;
  font-family: monospace;

  &:focus {
    outline: none;
    border-color: ${hasError ? "#ef4444" : "#059669"};
    box-shadow: 0 0 0 3px ${hasError ? "rgba(239, 68, 68, 0.15)" : "rgba(5, 150, 105, 0.15)"};
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: ${hasError ? "#ef4444" : "#334155"} !important;
  }
`;

const errorMessageText = css`
  margin: 0;
  font-size: 0.82rem;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const commandInputWrapper = css`
  position: relative;
  display: flex;
  align-items: center;
`;

const commandPrefixPill = css`
  position: absolute;
  left: 10px;
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.76rem;
  font-weight: 700;
  font-family: monospace;
  pointer-events: none;
  user-select: none;

  .theme--dark & {
    background: #064e3b !important;
    color: #6ee7b7 !important;
    border-color: #047857 !important;
  }
`;

const jsonHeaderRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const formatButton = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #cbd5e1 !important;

    &:hover {
      background: #334155 !important;
      color: #f8fafc !important;
    }
  }
`;

const examplesBox = css`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;

  .theme--dark & {
    background: #0b1329 !important;
    border-color: #1e293b !important;
  }
`;

const examplesTrigger = (expanded: boolean) => css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  background: transparent;
  border: none;
  font-size: 0.84rem;
  font-weight: 650;
  color: #475569;
  cursor: pointer;

  &:hover {
    color: #0f172a;
  }

  .theme--dark & {
    color: #94a3b8 !important;
    &:hover {
      color: #f8fafc !important;
    }
  }
`;

const examplesContent = css`
  padding: 12px 14px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .theme--dark & {
    border-color: #1e293b !important;
  }
`;

const exampleList = css`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const exampleListItem = css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;

  code {
    background: #e2e8f0;
    color: #0f172a;
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
    font-family: monospace;
  }

  span {
    color: #64748b;
  }

  .theme--dark & {
    code {
      background: #1e293b !important;
      color: #34d399 !important;
    }
    span {
      color: #94a3b8 !important;
    }
  }
`;

const jsonEditorContainer = (hasError = false) => css`
  border: 1px solid ${hasError ? "#ef4444" : "#cbd5e1"};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .theme--dark & {
    border-color: ${hasError ? "#ef4444" : "#334155"} !important;
  }
`;

const jsonEditorHeaderBar = css`
  background: #f1f5f9;
  padding: 6px 12px;
  border-bottom: 1px solid #cbd5e1;
  font-size: 0.76rem;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.05em;

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #94a3b8 !important;
  }
`;

const jsonTextarea = css`
  width: 100%;
  min-height: 120px;
  padding: 10px 12px;
  border: none;
  font-family: monospace;
  font-size: 0.86rem;
  line-height: 1.5;
  background: #ffffff;
  color: #0f172a;
  resize: vertical;

  &:focus {
    outline: none;
  }

  .theme--dark & {
    background: #0f172a !important;
    color: #f8fafc !important;
  }
`;

const envTableContainer = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const envRow = css`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const envInputKey = css`
  flex: 1;
  height: 38px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.88rem;
  font-family: monospace;
  color: #0f172a;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.15);
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: #334155 !important;
  }
`;

const envInputValue = css`
  flex: 1.5;
  height: 38px;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.88rem;
  font-family: monospace;
  color: #0f172a;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #059669;
    box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.15);
  }

  .theme--dark & {
    background: #1e293b !important;
    color: #f8fafc !important;
    border-color: #334155 !important;
  }
`;

const removeButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #fca5a5;
  }

  .theme--dark & {
    background: #450a0a !important;
    border-color: #7f1d1d !important;
    color: #f87171 !important;

    &:hover {
      background: #7f1d1d !important;
    }
  }
`;

const addVarButton = css`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.84rem;
  font-weight: 650;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
    color: #cbd5e1 !important;

    &:hover {
      background: #334155 !important;
      color: #f8fafc !important;
    }
  }
`;

const collapsibleCard = css`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;

  .theme--dark & {
    background: #0f172a !important;
    border-color: #1e293b !important;
  }
`;

const collapsibleTriggerButton = (open: boolean) => css`
  width: 100%;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  background: ${open ? "#f8fafc" : "transparent"};
  transition: background 0.15s ease-in-out;

  .theme--dark & {
    background: ${open ? "#1e293b" : "transparent"} !important;
  }
`;

const collapsibleTitle = css`
  margin: 0;
  font-size: 1rem;
  font-weight: 750;
  color: #0f172a;

  .theme--dark & {
    color: #f8fafc !important;
  }
`;

const collapsibleSubtitle = css`
  margin: 3px 0 0 0;
  font-size: 0.83rem;
  color: #64748b;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const collapsibleBody = css`
  padding: 18px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .theme--dark & {
    border-color: #1e293b !important;
  }
`;

const testConnectionSection = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;

  .theme--dark & {
    border-color: #1e293b !important;
  }
`;

const testResultSuccessPill = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  color: #065f46;
  font-size: 0.84rem;
  font-weight: 600;

  .theme--dark & {
    background: #064e3b !important;
    border-color: #047857 !important;
    color: #6ee7b7 !important;
  }
`;

const testResultErrorPill = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #991b1b;
  font-size: 0.84rem;
  font-weight: 600;

  .theme--dark & {
    background: #450a0a !important;
    border-color: #7f1d1d !important;
    color: #fca5a5 !important;
  }
`;

const bottomNavWrapper = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;

  .theme--dark & {
    border-color: #1e293b !important;
  }
`;

// ==========================================
// Main Component
// ==========================================

export function ExecutableTargetConfiguration({
  targetName = "support-agent",
  values,
  onChange,
  onBack,
  onNext,
  hideBottomBar = false,
  renderModeSwitcher,
  onTestConnection,
  isTestingConnection = false,
  testResult,
}: ExecutableTargetConfigurationProps) {
  // Collapsible toggle states
  const [examplesExpanded, setExamplesExpanded] = useState(false);
  const [testGenExpanded, setTestGenExpanded] = useState(false);
  const [advancedExpanded, setAdvancedExpanded] = useState(false);

  // Validation states
  const [commandError, setCommandError] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Format JSON handler
  const handleFormatJson = useCallback(() => {
    const raw = values.configJson?.trim();
    if (!raw) return;

    const res = formatExecutableConfigJson(raw);
    if (res.error) {
      setJsonError(res.error);
    } else if (res.formatted) {
      onChange({ configJson: res.formatted });
      setJsonError(null);
    }
  }, [values.configJson, onChange]);

  // Load Example JSON handler
  const handleLoadExampleJson = useCallback(() => {
    onChange({ configJson: JSON.stringify(EXECUTABLE_EXAMPLE_CONFIG, null, 2) });
    setJsonError(null);
  }, [onChange]);

  // Validate on Next click
  const handleNextClick = useCallback(() => {
    const { isValid, errors } = validateExecutableTargetConfig(values);

    if (errors.command) {
      setCommandError(errors.command);
    } else {
      setCommandError(null);
    }

    if (errors.configJson) {
      setJsonError(errors.configJson);
    } else {
      setJsonError(null);
    }

    if (isValid) {
      onNext();
    }
  }, [values, onNext]);

  // Environment variables array conversion
  const envList = useMemo(() => {
    const entries = Object.entries(values.env || {});
    if (entries.length === 0) {
      return [{ key: "", value: "" }];
    }
    return entries.map(([key, value]) => ({ key, value }));
  }, [values.env]);

  const handleEnvKeyChange = useCallback(
    (index: number, newKey: string) => {
      const currentList = [...envList];
      currentList[index].key = newKey;
      const newEnvObj: Record<string, string> = {};
      for (const item of currentList) {
        if (item.key.trim()) {
          newEnvObj[item.key.trim()] = item.value;
        }
      }
      onChange({ env: newEnvObj });
    },
    [envList, onChange]
  );

  const handleEnvValueChange = useCallback(
    (index: number, newVal: string) => {
      const currentList = [...envList];
      currentList[index].value = newVal;
      const newEnvObj: Record<string, string> = {};
      for (const item of currentList) {
        if (item.key.trim()) {
          newEnvObj[item.key.trim()] = item.value;
        }
      }
      onChange({ env: newEnvObj });
    },
    [envList, onChange]
  );

  const handleAddEnvVar = useCallback(() => {
    const newEnvObj = { ...(values.env || {}) };
    newEnvObj[""] = "";
    onChange({ env: newEnvObj });
  }, [values.env, onChange]);

  const handleRemoveEnvVar = useCallback(
    (keyToRemove: string) => {
      const newEnvObj = { ...(values.env || {}) };
      delete newEnvObj[keyToRemove];
      onChange({ env: newEnvObj });
    },
    [values.env, onChange]
  );

  // Command input handler
  const handleCommandChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ command: e.target.value });
      if (commandError) setCommandError(null);
    },
    [onChange, commandError]
  );

  const showExecPill = values.command?.startsWith("exec:");
  const commandInputPaddingLeft = showExecPill ? "62px" : "12px";

  return (
    <div css={pageWrapperStyle}>
      {/* 1. Header Row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          marginBottom: "8px",
        }}
      >
        <div>
          <Heading level={1} css={pageHeaderTitle}>
            {`Configure Target: ${targetName}`}
          </Heading>
          <div css={providerHeaderRow}>
            <ShellTargetIcon />
            <span css={providerBadgeTitle}>Shell Command / Custom Script Provider</span>
          </div>
        </div>
        {renderModeSwitcher && (
          <div style={{ flexShrink: 0, marginTop: "4px" }}>
            {renderModeSwitcher}
          </div>
        )}
      </div>

      {/* 2. Main Form Card Container */}
      <div css={formCardContainer}>
        {/* Field 1: Command (Required) */}
        <div css={formGroup}>
          <label css={fieldLabel} htmlFor="executable-command-input">
            Command <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div css={commandInputWrapper}>
            {showExecPill && <span css={commandPrefixPill}>exec:</span>}
            <input
              id="executable-command-input"
              type="text"
              css={textInput(!!commandError)}
              style={{ paddingLeft: commandInputPaddingLeft }}
              placeholder="exec:python /path/to/agent.py"
              value={values.command}
              onChange={handleCommandChange}
            />
          </div>
          {commandError && (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {commandError}
            </p>
          )}
          <p css={fieldHelpText}>
            Command or script path to execute. Prompts are delivered strictly via standard input (
            <code>stdin</code>). Prefix with <code>exec:</code> (e.g. <code>exec:python /path/to/script.py</code>).
          </p>
        </div>

        {/* Field 2: Timeout */}
        <div css={formGroup}>
          <label css={fieldLabel} htmlFor="executable-timeout-input">
            Execution Timeout (ms)
          </label>
          <input
            id="executable-timeout-input"
            type="number"
            min={100}
            step={1000}
            css={textInput()}
            placeholder="30000"
            value={values.timeout ?? 30000}
            onChange={(e) => onChange({ timeout: e.target.value })}
          />
          <p css={fieldHelpText}>
            Maximum execution time before the child process is terminated. Default: 30000 ms (30 seconds).
          </p>
        </div>

        {/* Field 3: Environment Variables */}
        <div css={formGroup}>
          <div css={jsonHeaderRow}>
            <label css={fieldLabel}>Environment Variables</label>
            <button type="button" css={addVarButton} onClick={handleAddEnvVar}>
              <PlusIcon />
              <span>Add Variable</span>
            </button>
          </div>
          <p css={fieldHelpText}>
            Configure custom environment variables passed to the process during execution.
            Sensitive keys and values are automatically redacted in logs.
          </p>

          <div css={envTableContainer}>
            {envList.map((item, idx) => (
              <div key={idx} css={envRow}>
                <input
                  type="text"
                  css={envInputKey}
                  placeholder="KEY (e.g. APP_ENV)"
                  value={item.key}
                  onChange={(e) => handleEnvKeyChange(idx, e.target.value)}
                />
                <input
                  type="text"
                  css={envInputValue}
                  placeholder="VALUE"
                  value={item.value}
                  onChange={(e) => handleEnvValueChange(idx, e.target.value)}
                />
                {Object.keys(values.env || {}).length > 0 && (
                  <button
                    type="button"
                    css={removeButton}
                    title="Remove variable"
                    onClick={() => handleRemoveEnvVar(item.key)}
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Field 4: Configuration (JSON) */}
        <div css={formGroup}>
          <div css={jsonHeaderRow}>
            <label css={fieldLabel} htmlFor="executable-config-json">
              Configuration (JSON)
            </label>
            <button
              type="button"
              css={formatButton}
              onClick={handleFormatJson}
              title="Format JSON with 2-space indentation"
            >
              <FormatAlignIcon />
              <span>Format</span>
            </button>
          </div>

          {/* Examples Collapsible */}
          <div css={examplesBox}>
            <button
              type="button"
              css={examplesTrigger(examplesExpanded)}
              onClick={() => setExamplesExpanded(!examplesExpanded)}
              aria-expanded={examplesExpanded}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CodeBracketsIcon />
                <span>Examples</span>
              </span>
              <ChevronDownIcon
                style={{
                  transform: examplesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.18s ease-in-out",
                }}
              />
            </button>

            {examplesExpanded && (
              <div css={examplesContent}>
                <div>
                  <div
                    style={{
                      fontSize: "0.84rem",
                      fontWeight: 700,
                      color: "#334155",
                      marginBottom: "6px",
                    }}
                  >
                    Supported Custom Script Formats
                  </div>
                  <ul css={exampleList}>
                    <li css={exampleListItem}>
                      <code>exec:python /path/to/agent.py</code>
                      <span>- Python script execution</span>
                    </li>
                    <li css={exampleListItem}>
                      <code>exec:./eval_target.sh</code>
                      <span>- Shell script execution</span>
                    </li>
                    <li css={exampleListItem}>
                      <code>exec:node script.js</code>
                      <span>- Node.js script execution</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "0.84rem",
                      fontWeight: 700,
                      color: "#334155",
                      marginBottom: "6px",
                    }}
                  >
                    JSON Config Example
                  </div>
                  <div
                    style={{
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "6px",
                      padding: "10px 12px",
                      fontSize: "0.82rem",
                      fontFamily: "monospace",
                      color: "#334155",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    <pre style={{ margin: 0 }}>
                      {JSON.stringify(EXECUTABLE_EXAMPLE_CONFIG, null, 2)}
                    </pre>
                    <button
                      type="button"
                      onClick={handleLoadExampleJson}
                      style={{
                        alignSelf: "flex-start",
                        padding: "3px 8px",
                        fontSize: "0.78rem",
                        fontWeight: 650,
                        background: "#059669",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Use This Example
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* JSON Editor Box */}
          <div css={jsonEditorContainer(!!jsonError)}>
            <div css={jsonEditorHeaderBar}>
              <span>JSON</span>
            </div>
            <textarea
              id="executable-config-json"
              css={jsonTextarea}
              placeholder="{}"
              value={values.configJson}
              onChange={(e) => {
                onChange({ configJson: e.target.value });
                if (jsonError) setJsonError(null);
              }}
              spellCheck={false}
            />
          </div>

          {jsonError ? (
            <p css={errorMessageText}>
              <AlertTriangleIcon />
              {jsonError}
            </p>
          ) : (
            <p css={fieldHelpText}>
              Additional custom parameters merged into provider configuration.
            </p>
          )}
        </div>

        {/* Test Connection Section */}
        {onTestConnection && (
          <div css={testConnectionSection}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
              <Button
                variant="secondary"
                size="medium"
                onClick={onTestConnection}
                disabled={isTestingConnection || !values.command.trim()}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <WifiIcon />
                  <span>{isTestingConnection ? "Testing Process..." : "Test Target Connection"}</span>
                </div>
              </Button>

              {testResult && (
                <div>
                  {testResult.success ? (
                    <span css={testResultSuccessPill}>
                      <CheckIcon />
                      <span>
                        Success {testResult.latency ? `(${testResult.latency})` : ""}
                      </span>
                    </span>
                  ) : (
                    <span css={testResultErrorPill}>
                      <CrossIcon />
                      <span>{testResult.message || "Connection failed"}</span>
                    </span>
                  )}
                </div>
              )}
            </div>
            <p css={fieldHelpText}>
              Executes a test diagnostic probe via stdin and verifies that the process returns cleanly.
            </p>
          </div>
        )}

        {/* Test Generation Collapsible */}
        <div css={collapsibleCard}>
          <button
            type="button"
            css={collapsibleTriggerButton(testGenExpanded)}
            onClick={() => setTestGenExpanded(!testGenExpanded)}
            aria-expanded={testGenExpanded}
          >
            <div>
              <h3 css={collapsibleTitle}>Test Generation</h3>
              <p css={collapsibleSubtitle}>
                Configure how red team test cases are generated for this executable target
              </p>
            </div>
            <ChevronDownIcon
              style={{
                transform: testGenExpanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.18s ease-in-out",
                color: "#64748b",
              }}
            />
          </button>

          {testGenExpanded && (
            <div css={collapsibleBody}>
              <div css={formGroup}>
                <label css={fieldLabel} htmlFor="executable-test-gen-input">
                  Instructions
                </label>
                <p css={fieldHelpText}>
                  Additional guidance for generating adversarial red team attacks against this script.
                </p>
                <textarea
                  id="executable-test-gen-input"
                  css={textInput()}
                  style={{
                    minHeight: "80px",
                    height: "auto",
                    padding: "10px 14px",
                    resize: "vertical",
                  }}
                  placeholder="Focus on command injection and unauthorized file access probes"
                  value={values.testGenerationInstructions || ""}
                  onChange={(e) => onChange({ testGenerationInstructions: e.target.value })}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3. Bottom Navigation Bar */}
      {!hideBottomBar && (
        <div css={bottomNavWrapper}>
          <Button variant="secondary" onClick={onBack}>
            Back
          </Button>
          <Button variant="primary" onClick={handleNextClick}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
